import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import { getShiftNoteById, postShiftNote, putShiftNote } from 'core/API/logbook';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDocumentContext } from 'module/documents/context';

const AddShiftNotes = () => {
  const { showToast } = useToastContext();
  const { noteId } = useParams<{ noteId?: string }>();
  const history = useHistory();
  const workplace = useWorkplaceUser();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');
  const { startDate } = useDocumentContext();
  const userFullName = workplace?.data? 
    `${workplace.data.firstName || ''} ${workplace.data.lastName || ''}`.trim() || 'Unknown User' : 
    'Unknown User';
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [authorName, setAuthorName] = useState('');

  // Fetch only the target note by ID for edit mode
  const { data: shiftNoteById, isLoading: isNotesLoading, isError: isNoteFetchError } = useQuery(
    ['shiftNoteById', noteId],
    () => getShiftNoteById(parseInt(noteId as string, 10)),
    { 
      enabled: !!noteId,
      staleTime: 0,
      refetchOnMount: true
    }
  );

  useEffect(() => {
    if (!noteId) return;
    if (isNotesLoading) return;
    if (isNoteFetchError || !shiftNoteById) {
      setError('Note not found for editing');
      return;
    }

    const noteContent = shiftNoteById.note || '';
    setTimeout(() => {
      setNote(noteContent);
    }, 100);

    if (shiftNoteById.author && typeof shiftNoteById.author === 'object') {
      const firstName = shiftNoteById.author.firstName || '';
      const lastName = shiftNoteById.author.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      setAuthorName(fullName || 'Unknown User');
    } else {
      setAuthorName('Unknown User');
    }
  }, [noteId, shiftNoteById, isNotesLoading, isNoteFetchError]);

  const { mutate: createNote, isLoading: isCreating } = useMutation(postShiftNote, {
    onSuccess: () => {
      showToast('success', 'Shift Note added!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to add Shift Note.');
    },
  });

  const { mutate: updateNote, isLoading: isUpdating } = useMutation(putShiftNote, {
    onSuccess: () => {
      showToast('success', 'Shift Note updated!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to update Shift Note.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      setError('Shift note cannot be empty.');
      return;
    }
    setError('');

    // Convert startDate (YYYY-MM-DD) to ISO format with time
    // Same logic as used in sales/traffic
    let dateISO: string | undefined;
    if (startDate) {
      dateISO = new Date(startDate).toISOString();
    }

    // Prepare note content for submission
    if (noteId) {
      // For editing, we need the ID and note
      updateNote({ id: parseInt(noteId), note, workplaceId, date: dateISO });
    } else {
      // For creating, we only need the note
      createNote({ note, workplaceId, date: dateISO });
    }
  };

  // Show loading state while fetching note data for editing
  if (noteId && isNotesLoading) {
    return (
      <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6">Loading note for editing...</Typography>
      </Box>
    );
  }

  // Show error if note not found for editing
  if (noteId && error === 'Note not found for editing') {
    return (
      <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box textAlign="center">
          <Typography variant="h6" color="error" mb={2}>Note not found</Typography>
          <Button 
            onClick={() => history.push(ROUTES.LOGBOOK)}
            variant="contained"
            sx={{ backgroundColor: 'rgb(68, 99, 136)' }}
          >
            Back to Logbook
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Box maxWidth="1100px" mx="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
            onClick={() => history.goBack()}
            sx={{
            backgroundColor: 'rgb(68, 99, 136)',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 0.75,
            '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
        >
            Back
        </Button>

        <Button
            type="submit"
            variant="contained"
            form="shiftNoteForm"
            sx={{
            backgroundColor: 'rgb(68, 99, 136)',
            color: '#fff',
            px: 4,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
            disabled={isCreating || isUpdating}
        >
            {isCreating || isUpdating ? 'Saving…' : 'Save'}
        </Button>
        </Box>

        <Paper elevation={1}>
                    
        <Box
            sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderBottom: 1,
                borderColor: 'divider',
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
            }}
            >
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={600}>
                {noteId ? 'Edit Shift Note' : 'Add Shift Note'}
                </Typography>
            </Box>
        </Box>
        {noteId && (
            <Box
              sx={{
                px: 3,        // same left / right padding as form body
                pt: 1.5,        // a little space under the header bar
                pb: 0.1         // space before the TextField starts
              }}
            >
              <Typography variant="subtitle2">
                <strong>{authorName}</strong> — Editing existing note
              </Typography>
            </Box>
        )}
          <Box sx={{ p: 3, minHeight: 350 }}>
            <form id="shiftNoteForm" onSubmit={handleSubmit}>
              <Box sx={{ mb: 2 }}>
                  <ReactQuill
                    key={noteId || 'new'}
                    value={note}
                  onChange={(content) => {
                      // console.log("here is content", content);
                      setNote(content);
                    }}
                    placeholder="Enter shift note here"
                    modules={{ 
                      toolbar: [['bold'], [{ list: 'bullet' }]],
                      clipboard: {
                        matchVisual: false
                      }
                    }}
                    formats={['bold', 'list', 'bullet', 'indent', 'align', 'script', 'header']}
                    preserveWhitespace={true}
                    className="quill-editor"
                    style={{
                      backgroundColor: 'white',
                      height: '250px', // FIXED height
                      // overflowY: 'auto', // enable scroll inside
                    }}
                  />
                  {error && (
                    <Typography variant="caption" color="error">
                      {error}
                    </Typography>
                  )}
                </Box>
                <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                  {/* <Button
                    onClick={() => history.push(ROUTES.LOGBOOK)}
                    sx={{ color: '#7a1b8d', textTransform: 'none' }}
                  >
                    Cancel
                  </Button> */}
                  {/* <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: '#263238',
                      color: 'white',
                      px: 4,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#1a242b' },
                    }}
                    disabled={isCreating || isUpdating}
                  >
                    {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
                  </Button> */}
                </Box>
              </form>
            </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddShiftNotes;
